import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  Button,
  Input,
  Checkbox,
  Label,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert } from '@vnetwork/ui/feedback';
import {
  useCreateContactAccount,
  type CreateContactAccountPayload,
} from '@/api/customer-basic';

export interface CreateAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contactId: number;
  contactEmail?: string;
}

/**
 * Create a Keycloak account for a contact. Collects a password (min 8) + a
 * confirmation and a "send welcome email" toggle, then POSTs via
 * `useCreateContactAccount`. Validation mirrors the legacy `createAccountSchema`.
 */
export function CreateAccountDialog({ open, onOpenChange, contactId, contactEmail }: CreateAccountDialogProps) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [sendWelcome, setSendWelcome] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const create = useCreateContactAccount(contactId);

  useEffect(() => {
    if (open) {
      setPassword('');
      setConfirm('');
      setSendWelcome(true);
      setError(null);
    }
  }, [open]);

  const tooShort = password.length > 0 && password.length < 8;
  const mismatch = confirm.length > 0 && confirm !== password;

  const handleSubmit = () => {
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (confirm !== password) {
      setError('Passwords must match.');
      return;
    }
    setError(null);
    const payload: CreateContactAccountPayload = {
      password,
      password_confirmation: confirm,
      send_welcome_email: sendWelcome,
    };
    create.mutate(payload, {
      onSuccess: () => onOpenChange(false),
      onError: () => setError('Failed to create the account. Please try again.'),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Create account</DialogTitle>
          {contactEmail ? <DialogDescription>{contactEmail}</DialogDescription> : null}
        </DialogHeader>
        <DialogBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            {error ? <Alert tone="danger" title={error} /> : null}
            <Field label="Password" required error={tooShort ? 'At least 8 characters' : undefined}>
              {(p) => (
                <Input
                  {...p}
                  type="password"
                  value={password}
                  invalid={tooShort}
                  autoComplete="new-password"
                  onChange={(e) => setPassword(e.target.value)}
                />
              )}
            </Field>
            <Field label="Confirm password" required error={mismatch ? 'Passwords must match' : undefined}>
              {(p) => (
                <Input
                  {...p}
                  type="password"
                  value={confirm}
                  invalid={mismatch}
                  autoComplete="new-password"
                  onChange={(e) => setConfirm(e.target.value)}
                />
              )}
            </Field>
            <Label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--vn-space-2)',
                fontSize: 'var(--vn-font-size-sm)',
                cursor: 'pointer',
              }}
            >
              <Checkbox
                checked={sendWelcome}
                onCheckedChange={(v) => setSendWelcome(v === true)}
              />
              Send welcome email
            </Label>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={create.isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={create.isPending}>
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CreateAccountDialog;
